import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import countriesAndTimezones from 'countries-and-timezones';
import { UsaStates } from 'usa-states';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';

import { Field, FieldArray, reduxForm, SubmissionError } from 'redux-form';

import Section, { SectionHeader, SectionTitle, SectionBody } from 'components/common/Section';
import Button from 'components/common/Button';
import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';
import ScheduleFieldArray from 'components/fields/ScheduleFieldArray';
import CheckboxField from 'components/fields/CheckboxField';
import AutosuggestField from 'components/fields/AutosuggestField';
import UploadField from 'components/fields/UploadField';
import FormError from 'components/common/FormError';

import { requestCreateSchedule, requestUpdateSchedule } from 'redux/modules/schedule';
import { createFacility, updateFacility } from 'redux/modules/facilities';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';

import timeFormat from 'config/timeFormat';
import imageFileTypes from 'config/imageFileTypes';

import { requestFetchPlacesAutocomplete } from 'redux/modules/placesAutocomplete';

const usaStates = new UsaStates();
const usaTimeZones = countriesAndTimezones.getTimezonesForCountry('US')
  .map(item => item.name);

const styles = theme => ({
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  gridGutter: {
    marginTop: 15,
  },
  button: {
    marginLeft: 20,
  },
  form: {
    width: 730,
    margin: '10px auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  typography: {
    paddingBottom: 2,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  sectionBody: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 24,
    },
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues, {
      ...formValues['Trading Partner ID'] === '' ? {
        'Trading Partner ID': null,
      } : {},
      ...formValues.MEDICAID === '' ? {
        MEDICAID: null,
      } : {},
      ...formValues.MEDICARE === '' ? {
        MEDICARE: null,
      } : {},
    });
    delete normalizedValues['Work Schedule'];
    return normalizedValues;
  };
  if (props.pristine) {
    throw new SubmissionError({
      _error: 'Nothing to submit',
    });
  }
  if (props.ID) {
    const diffValues = objectDiff(values, props.initialValues);
    if (diffValues['Work Schedule']) {
      return Promise.all(
        values['Work Schedule'].map((schedule) => {
          const normalizedSchedule = Object.assign({}, schedule, {
            From: moment(schedule.From, timeFormat).format(timeFormat),
            To: moment(schedule.To, timeFormat).format(timeFormat),
          });
          if (normalizedSchedule.ID) {
            return requestUpdateSchedule(normalizedSchedule);
          }
          return requestCreateSchedule(normalizedSchedule);
        }),
      )
        .then(resArray => new Promise((resolve, reject) => {
          dispatch(updateFacility({
            values: {
              ID: props.initialValues.ID,
              ...normalizeValues(diffValues),
              'Work Schedule ID': resArray.map(item => item.data.ID),
            },
            resolve,
            reject,
          }));
        }).then(res => res,
          (err) => {
            if (err.error) {
              throw new SubmissionError({ _error: err.error });
            }
            throw new SubmissionError(err);
          }));
    }
    return new Promise((resolve, reject) => {
      dispatch(updateFacility({
        values: {
          ID: props.initialValues.ID,
          ...diffValues['Company Info'] ? { 'Company Info': {
            ID: values['Company Info'].ID,
            ...diffValues['Company Info'],
          } } : {},
          ...diffValues['Company Contact'] ? { 'Company Contact': {
            ID: values['Company Contact'].ID,
            ...diffValues['Company Contact'],
            ...diffValues['Company Contact']['Company Website'] === '' ? {
              'Company Website': null,
            } : {},
          } } : {},
          ...diffValues['Company Address'] ? { 'Company Address': {
            ...values['Company Address'].ID ? {
              ID: values['Company Address'].ID,
            } : {
              'Facility ID': props.initialValues.ID,
            },
            ...diffValues['Company Address'],
            'Observes Daylight Saving': values['Company Address']['Observes Daylight Saving'] || false,
          } } : {},
          ...diffValues['Company Identify'] ? { 'Company Identify': {
            ...values['Company Identify'].ID ? {
              ID: values['Company Identify'].ID,
            } : {
              'Facility ID': props.initialValues.ID,
            },
            ...diffValues['Company Identify'],
            ...diffValues['Company Identify']['Trading Partner ID'] === '' ? {
              'Trading Partner ID': null,
            } : {},
            ...diffValues['Company Identify'].MEDICAID === '' ? {
              MEDICAID: null,
            } : {},
            ...diffValues['Company Identify'].MEDICARE === '' ? {
              MEDICARE: null,
            } : {},
            ...diffValues['Company Identify']['Primary Taxonomy'] === '' ? {
              'Primary Taxonomy': null,
            } : {},
          } } : {},
          ...diffValues.Image ? { Image: values.Image } : {},
          ...diffValues.Image === null ? { Image: null } : {},
        },
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.error });
        }
        throw new SubmissionError(err);
      });
  }
  return Promise.all(
    values['Work Schedule'].map(schedule => requestCreateSchedule({
      ...schedule,
      From: moment(schedule.From, timeFormat).format(timeFormat),
      To: moment(schedule.To, timeFormat).format(timeFormat),
    })),
  )
    .then(resArray => new Promise((resolve, reject) => {
      dispatch(createFacility({
        values: {
          'Work Schedule ID': resArray.map(item => item.data.ID),
          ...values['Company Info'] ? { 'Company Info': values['Company Info'] } : {},
          ...values['Company Contact'] ? { 'Company Contact': {
            ...values['Company Contact'],
            ...values['Company Contact']['Company Website'] === '' ? {
              'Company Website': null,
            } : {},
          } } : {},
          ...values['Company Address'] ? { 'Company Address': {
            ...values['Company Address'],
            'Observes Daylight Saving': values['Company Address']['Observes Daylight Saving'] || false,
          } } : {},
          ...values['Company Identify'] ? { 'Company Identify': {
            ...values['Company Identify'],
            ...values['Company Identify']['Trading Partner ID'] === '' ? {
              'Trading Partner ID': null,
            } : {},
            ...values['Company Identify'].MEDICAID === '' ? {
              MEDICAID: null,
            } : {},
            ...values['Company Identify'].MEDICARE === '' ? {
              MEDICARE: null,
            } : {},
            ...values['Company Identify']['Primary Taxonomy'] === '' ? {
              'Primary Taxonomy': null,
            } : {},
          } } : {},
          ...values.Image ? { Image: values.Image } : {},
          ...values.Image === null ? { Image: null } : {},
        },
        resolve,
        reject,
      }));
    }))
    .then(res => res.data,
      (errors) => {
        if (errors.data) {
          if (errors.data.error) {
            throw new SubmissionError({ _error: errors.data.error });
          }
          throw new SubmissionError(errors.data);
        }
        if (errors.error) {
          throw new SubmissionError({ _error: errors.error });
        }
        throw new SubmissionError(errors);
      });
}

function onSubmitFail(errors) {
  let errorNames = Object.keys(errors).filter(item => item !== 'Work Schedule');
  const workScheduleErrorNames = errors['Work Schedule']
    ? errors['Work Schedule'].reduce((accumulator, currentValue, index) => {
      const currentWorkScheduleErrorNames = currentValue ? Object.keys(currentValue).map(item =>
        `Work Schedule[${index}].${item}`) : [];
      if (currentWorkScheduleErrorNames.length > 0) {
        return [
          ...accumulator,
          ...currentWorkScheduleErrorNames,
        ];
      }
      return accumulator;
    }, []) : [];
  errorNames = [
    ...workScheduleErrorNames,
    ...errorNames,
  ];
  if (errorNames.length > 0) {
    const selectorString = errorNames.map(item => `[name="${item}"]`).join(', ');
    const firstErrorEl = document.querySelector(selectorString);
    if (firstErrorEl) {
      const firstErrorElOffsetTop = firstErrorEl.getBoundingClientRect().top;
      const windowScrollTop = window.scrollY !== undefined ? window.scrollY : window.scrollTop;
      const scrollOffset = 50;
      if ((window.innerHeight - scrollOffset) < firstErrorElOffsetTop) {
        const pageHeaderHeight = 64;
        window.scrollTo(0, (firstErrorElOffsetTop
          + windowScrollTop) - pageHeaderHeight - scrollOffset);
        firstErrorEl.focus();
      }
    }
  }
}

const validateForm = (values) => {
  const errors = {};
  if (values['Company Address'] && values['Company Address']['Time Zone'] && usaTimeZones.indexOf(values['Company Address']['Time Zone']) === -1) {
    errors['Company Address'] = {
      'Time Zone': 'Please, select Time Zone from list',
    };
  }

  return errors;
};

class FacilityCreateEditForm extends Component {
  constructor(props) {
    super(props);

    const customiseSchedule =
      !(props.initialValues['Work Schedule'].length === 2
      && props.initialValues['Work Schedule'][0]['Weekday ID']
      && props.initialValues['Work Schedule'][0]['Weekday ID'].join('') === '23456'
      && props.initialValues['Work Schedule'][1]['Weekday ID']
      && props.initialValues['Work Schedule'][1]['Weekday ID'].join('') === '71');

    this.state = {
      customiseSchedule,
    };

    this.onCustomiseScheduleButtonClick = this.onCustomiseScheduleButtonClick.bind(this);
  }

  onCustomiseScheduleButtonClick() {
    this.setState({ customiseSchedule: true });
  }

  render() {
    const { submit, submitting, change, handleSubmit, error, ID, classes, width } = this.props;
    const { customiseSchedule } = this.state;
    return (
      <Section>
        <SectionHeader fixed top={110}>
          <SectionTitle>
            {ID ? 'Edit Facility' : 'Add Facility'}
          </SectionTitle>
          <div>
            <Button
              className={classes.button}
              variant="raised"
              href="/settings/business/facilities"
            >
              Cancel
            </Button>
            <Button
              className={classes.button}
              variant="raised"
              color="primary"
              onClick={submit}
              disabled={submitting}
            >
              Save
            </Button>
          </div>
        </SectionHeader>
        <SectionBody className={classes.sectionBody}>
          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            {error &&
              <Grid
                className={classes.grid}
                container
                spacing={24}
              >
                <Grid
                  item
                  xs={12}
                >
                  <FormError>
                    {error}
                  </FormError>
                </Grid>
              </Grid>
            }
            <Grid
              className={classNames(classes.grid, classes.gridGutter)}
              container
              spacing={24}
            >
              <Grid item xs={12}>
                <Typography className={classes.typography}>
                  Contact Information
                </Typography>
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Field
                  id="Company Name"
                  name="Company Info.Company Name"
                  label="Facility Name"
                  component={TextField}
                  validate={[validate.required]}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  id="Signer First Name"
                  name="Company Info.Signer First Name"
                  label="Signer First Name"
                  component={TextField}
                  validate={[validate.required]}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  id="Signer Last Name"
                  name="Company Info.Signer Last Name"
                  label="Signer Last Name"
                  component={TextField}
                  validate={[validate.required]}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              alignItems="flex-end"
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Field
                  id="Phone Number"
                  name="Company Contact.Phone Number"
                  label="Phone Number"
                  component={TextField}
                  validate={[validate.required, validate.phone]}
                  format={value => (value ? value.replace('+1 ', '') : '')}
                  normalize={normalize.phone}
                  maxLength="14"
                  startAdornment={
                    <InputAdornment position="start">
                      <TextFieldAdornmentText>
                        +1
                      </TextFieldAdornmentText>
                    </InputAdornment>
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  id="Fax"
                  name="Company Contact.Fax"
                  label="Fax"
                  component={TextField}
                  validate={[validate.required, validate.phone]}
                  format={value => (value ? value.replace('+1 ', '') : '')}
                  normalize={normalize.phone}
                  maxLength="14"
                  startAdornment={
                    <InputAdornment position="start">
                      <TextFieldAdornmentText>
                        +1
                      </TextFieldAdornmentText>
                    </InputAdornment>
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  id="Time Zone"
                  name="Company Address.Time Zone"
                  label="Time Zone"
                  component={AutosuggestField}
                  validate={[validate.required]}
                  placeholder="-Select Title-"
                  fullWidth
                  renderSuggestionContent={suggestion => suggestion}
                  onSuggestionSelected={suggestion => suggestion}
                  fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                    if (offset === 0) {
                      if (value.length > 0) {
                        resolve(usaTimeZones
                          .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                      } else {
                        resolve(usaTimeZones);
                      }
                    } else {
                      resolve([]);
                    }
                  }))}
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              alignItems="flex-end"
              spacing={24}
            >
              <Grid item xs={6}>
                <Field
                  id="Observes daylight saving"
                  name="Company Address.Observes Daylight Saving"
                  label="Observes daylight saving"
                  component={CheckboxField}
                />
              </Grid>
            </Grid>
            <Grid
              className={classNames(classes.grid, classes.gridGutter)}
              container
              spacing={24}
            >
              <Grid item xs={12}>
                <Typography className={classes.typography}>
                  Facility Logo
                </Typography>
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid
                item
                xs={10}
              >
                <Field
                  id="Image"
                  name="Image"
                  component={UploadField}
                  variant="image"
                  maxSize={1000000}
                  accept={imageFileTypes}
                  label={(
                    <Fragment>
                      Drag file here <br />
                      JPG, PNG or GIF <br />
                      No larger than 1 MB <br />
                    </Fragment>
                  )}
                  fullWidth
                  showClearButton
                />
              </Grid>
            </Grid>
            <Grid
              className={classNames(classes.grid, classes.gridGutter)}
              container
              spacing={24}
            >
              <Grid item xs={12}>
                <Typography className={classes.typography}>
                  Identifiers
                </Typography>
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Field
                  id="Practice NPI"
                  name="Company Identify.Practice NPI"
                  label="Practice NPI"
                  component={TextField}
                  validate={[validate.required, validate.minLength10]}
                  maxLength="10"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  id="EIN"
                  name="Company Identify.EIN"
                  label="EIN"
                  component={TextField}
                  validate={[validate.required]}
                  maxLength="9"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  id="Trading Partner ID"
                  name="Company Identify.Trading Partner ID"
                  label="Trading Partner ID"
                  component={TextField}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Field
                  id="MEDICARE"
                  name="Company Identify.MEDICARE"
                  label="MEDICARE"
                  component={TextField}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  id="MEDICAID"
                  name="Company Identify.MEDICAID"
                  label="MEDICAID"
                  component={TextField}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  id="Primary Taxonomy"
                  name="Company Identify.Primary Taxonomy"
                  label="Primary Taxonomy"
                  component={TextField}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid
              className={classNames(classes.grid, classes.gridGutter)}
              container
              spacing={24}
            >
              <Grid item xs={12}>
                <Typography className={classes.typography}>
                  Facility Address
                </Typography>
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={9} md={6}>
                <Field
                  id="Street"
                  name="Company Address.Street"
                  label="Street"
                  component={AutosuggestField}
                  validate={[validate.required]}
                  placeholder="Lookup Address"
                  fullWidth
                  renderSuggestionContent={(suggestion) => suggestion.address.label}
                  onSuggestionSelected={(suggestion) => {
                    if (suggestion.address.city) {
                      change('Company Address.City', suggestion.address.city);
                    }
                    if (suggestion.address.postalCode) {
                      change('Company Address.Zip Code', suggestion.address.postalCode);
                    }
                    if (suggestion.address.stateCode) {
                      change('Company Address.State', suggestion.address.stateCode);
                    }
                    if (suggestion.address.street) {
                      if (suggestion.address.houseNumber) {
                        return `${suggestion.address.houseNumber} ${suggestion.address.street}`;
                      }
                      return suggestion.address.street;
                    }
                    return suggestion.address.label;
                  }}
                  fetchSuggestions={({ value }) => (
                    value.length > 0
                      ? requestFetchPlacesAutocomplete({
                        q: value,
                        in: 'countryCode:USA',
                        limit: 5,
                      }).then(res => res.items,
                        err => err)
                      : new Promise(resolve => resolve([]))
                  )}
                />
              </Grid>
              <Grid item xs={3} md={2}>
                <Field
                  id="Suite"
                  name="Company Address.Suite"
                  label={width === 'xs' || width === 'sm' ? 'Suite' : 'Suite (Optional)'}
                  component={TextField}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={6} md={4}>
                <Field
                  id="City"
                  name="Company Address.City"
                  label="City"
                  component={TextField}
                  validate={[validate.required]}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3} md={2}>
                <Field
                  id="State"
                  name="Company Address.State"
                  label="State"
                  component={AutosuggestField}
                  normalize={normalize.uppercase}
                  fullWidth
                  renderSuggestionContent={suggestion => suggestion.abbreviation}
                  onSuggestionSelected={suggestion => suggestion.abbreviation}
                  fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                    if (offset === 0) {
                      if (value.length > 0) {
                        resolve(usaStates.states
                          .filter(item => item.abbreviation.indexOf(value) === 0));
                      } else {
                        resolve(usaStates.states);
                      }
                    } else {
                      resolve([]);
                    }
                  }))}
                />
              </Grid>
              <Grid item xs={3} md={2}>
                <Field
                  id="Zip Code"
                  name="Company Address.Zip Code"
                  label="Zip Code"
                  component={TextField}
                  validate={[validate.required]}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid
              className={classNames(classes.grid, classes.gridGutter)}
              container
              alignItems="center"
              spacing={16}
            >
              <Grid item>
                <Typography className={classes.typography}>
                  Work Schedule: {customiseSchedule && 'Customise'}
                </Typography>
              </Grid>
              {!customiseSchedule &&
                <Grid item>
                  <Button
                    variant="raised"
                    onClick={this.onCustomiseScheduleButtonClick}
                  >
                    Customise
                  </Button>
                </Grid>
              }
            </Grid>
            <Grid
              className={classNames(classes.grid, classes.gridGutter)}
              container
              spacing={24}
            >
              <Grid item xs={12}>
                <FieldArray
                  id="Work Schedule"
                  name="Work Schedule"
                  component={ScheduleFieldArray}
                  customise={customiseSchedule}
                />
              </Grid>
            </Grid>
          </form>
        </SectionBody>
      </Section>
    );
  }
}

FacilityCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  ID: PropTypes.number,
  error: PropTypes.string,
  initialValues: PropTypes.shape({
    'Work Schedule': PropTypes.arrayOf(PropTypes.shape({
      'Weekday ID': PropTypes.arrayOf(PropTypes.number),
    })),
  }).isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    gridGutter: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    form: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
  }).isRequired,
};

FacilityCreateEditForm.defaultProps = {
  ID: null,
  error: null,
};

export default reduxForm({
  onSubmit,
  onSubmitFail,
  validate: validateForm,
  initialValues: {
    'Observes Daylight Saving': false,
    'Work Schedule': [
      {
        'Weekday ID': [2, 3, 4, 5, 6],
      },
      {
        'Weekday ID': [7, 1],
      },
    ],
  },
})(withStyles(styles)(withWidth()(FacilityCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/settings/FacilityCreateEditForm/FacilityCreateEditForm.js